import { Abstract, AbstractAuthor, AbstractSettings, AbstractType, ReviewerStats } from ".";
import { List } from "immutable";
import * as moment from "moment";
import { AbstractStatuses } from "./AbstractStatus";
import { ComposeUploads } from "../../forms/abstract";
import { Api } from "../../services/Api";
import { Utility } from "../../utils";
import { SubEvent } from "../event";
import { PersonHelper } from "../generic";
import { MemberHelper, MemberStatuses } from "../member";
import { FieldValidationError, IFieldValidationError } from "../validation";

export const checkAbstractAccess = async (api: Api, guid: string) => {
    const result = await api.get(Api.endpoints.users.abstractGrading.abstractAccess.withUrlParams({ guid }));
    return result.isSuccess && result.data !== undefined && result.data;
};

export const checkReviewerAccess = async (props: {
    api: Api;
    reviewer?: ReviewerStats;
    saveReviewerStats: (r: ReviewerStats) => void;
}) => {
    if (props.reviewer && !props.reviewer.isReviewer)
        return false;

    if (!props.reviewer) {
        const reviewer = await getReviewerStats(props.api);
        if (!reviewer || !reviewer.isReviewer) {
            return false;
        }

        props.saveReviewerStats(reviewer);
    }

    return true;
};

export const deadlineDisplay = (deadline: string) => {
    return moment(Utility.utcToLocal(deadline));
};

export const findEventWithScheduledAbstract = (abstractId: number, subEvents: SubEvent[]) => {
    let subEvt: SubEvent | undefined;
    for (const evt of subEvents) {
        if (evt.scheduledAbstractId === abstractId) {
            subEvt = evt;
            break;
        }

        // check children
        if (evt.children) {
            subEvt = findEventWithScheduledAbstract(abstractId, evt.children);
            if (subEvt)
                break;
        }
    }

    return subEvt;
};

export const getAbstractSettings = async (api: Api, eventId: number) => {
    const result = await api.get(Api.endpoints.abstract.settings.withUrlParams({ eventId }));
    return result.isSuccess && result.data;
};

export const getReviewerStats = async (api: Api) => {
    const result = await api.get(Api.endpoints.users.abstractGrading.reviewerStats);
    return result.isSuccess && result.data ? result.data : undefined;
};

export const getUserStats = async (api: Api, eventId: number) => {
    const result = await api.get(Api.endpoints.abstract.userStats.withUrlParams({ eventId }));
    return result.isSuccess && result.data;
};

export const isAllowedForInvitationResponse = (a: Abstract) => {
    if (a.statusId !== AbstractStatuses.Accepted.id)
        return false;
    if (a.isCoi)
        return false;
    if (!a.invitationSent)
        return false;
    if (Utility.utcToLocalAsMoment(a.invitationDeadline || "").toDate().valueOf() < new Date().valueOf())
        return false;

    return true;
};

export const isAllowedForInvitationSending = (a: Abstract) => {
    if (a.statusId !== AbstractStatuses.Accepted.id)
        return false;
    if (a.isCoi)
        return false;

    return true;
};

export const isSubmissionsOpen = (settings: AbstractSettings) => {
    if (!settings.submissionsOpenOn || !settings.openAbstractTypes)
        return false;
    const deadline = moment(Utility.utcToLocal(settings.submissionsOpenOn));
    return deadline.diff(moment(), "hours", true) < 0;
};

export const isSubmitDeadlinePassed = (type: string | AbstractType) => {
    const deadlineStr = typeof type === "string" ? type : type.submitDeadline;
    if (!deadlineStr)
        return false;
    const deadline = moment(Utility.utcToLocal(deadlineStr));
    return deadline.diff(moment(), "hours", true) < 0;
};

export const isUploadDeadlinePassed = (type: string | AbstractType) => {
    const deadlineStr = typeof type === "string" ? type : type.uploadDeadline;
    if (!deadlineStr)
        return false;
    const deadline = moment(Utility.utcToLocal(deadlineStr));
    return deadline.diff(moment(), "hours", true) < 0;
};

export const memberStatusIsAllowed = async (api: Api, userId: number) => {
    const result = await MemberHelper.getStatus(api, userId);
    return result.isSuccess && result.data && MemberStatuses.isActiveOrApplicant(result.data.memberStatusId || 0);
};

export const userHasAccessToAbstract = async (api: Api, userId: number, abstractGuid: string) => {
    const result = await api.get(Api.endpoints.abstract.hasAccess.withUrlParams({ guid: abstractGuid }));
    return result.isSuccess && result.data;
};

export const validateAbstractForSubmission = (abstract: Abstract, type: AbstractType) => {
    const errors: IFieldValidationError[] = [];

    // authorship errors
    if (abstract.typeId <= 0)
        errors.push(new FieldValidationError("authorship", "Presentation format"));
    if (Utility.isNullOrEmptyString(abstract.title))
        errors.push(new FieldValidationError("authorship", "Title"));

    const authors = List(abstract.authors);
    const authorMembers = authors.filter((x: AbstractAuthor) => x.isMember).size;
    const primaryAuthor = authors.filter((x: AbstractAuthor) => x.isPrimary).first();

    if (!primaryAuthor)
        errors.push(new FieldValidationError("authorship", "Presenting author"));
    else {
        if (!primaryAuthor.isMember && type.requiresMemberAuthor && authorMembers === 0)
            errors.push(new FieldValidationError("authorship", "Co-authors must include an ASRS member"));
        if (!primaryAuthor.phone || Utility.isNullOrEmptyString(primaryAuthor.phone.number))
            errors.push(new FieldValidationError("authorship", "Presenting author's mobile number"));
        if (type.coAuthorsMembersOnly) {
            authors.forEach((a: AbstractAuthor) => {
                if (!a.isPrimary && !a.isMember)
                    errors.push(new FieldValidationError("authorship", `${PersonHelper.getFullName(a)} is not an ASRS Member`));
            });
        }
    }

    // details errors
    if (type.classifications.length && !abstract.classificationId)
        errors.push(new FieldValidationError("details", "Classification"));

    if (type.form) {
        const answers = abstract.details
            ? List(abstract.details.answers)
            : List([]);

        const requiredFields = type.form.sections
            .map((s) => s.fields)
            .reduce((a, b) => (a.concat(b)))
            .filter((x) => x.metaData.isRequired);

        for (const f of requiredFields) {
            const answer = answers.find((x) => x !== undefined && x.fieldId === f.fieldId);
            if (!answer || answer.answer === null || answer.answer === undefined)
                errors.push(new FieldValidationError("details", f.metaData.label));
        }
    }

    // uploads errors
    if (type.typeMedia) {
        for (const tm of type.typeMedia) {
            const media = abstract.media.filter((m) => m.abstractTypeMediaId === tm.id);
            if (tm.requiredForSubmit && !type.uploadDeadline) {
                if (!!tm.minUploads && media.length < tm.minUploads)
                    errors.push(new FieldValidationError("uploads", `${tm.label} requires a minimum of ${tm.minUploads} upload${tm.minUploads === 1 ? "" : "s"}`));
                if (!!tm.maxUploads && media.length > tm.maxUploads)
                    errors.push(new FieldValidationError("uploads", `${tm.label} requires a maximum of ${tm.maxUploads} upload${tm.maxUploads === 1 ? "" : "s"}`));
                if (media.length === 0)
                    errors.push(new FieldValidationError("uploads", `${tm.label} requires an upload`));
            }
        }
    }

    return errors;
};

export const validateUploadsStep = (formModel: ComposeUploads, typeHasUploadDeadline: boolean) => {
    const errors: IFieldValidationError[] = [];
    for (const tm of formModel.data.typeMedia) {
        if (tm.requiredForSubmit && !typeHasUploadDeadline) {
            const max = tm.maxUploads;
            const min = tm.minUploads;
            if (!!min && tm.media.filter((m) => !m.deleted && m.id > 0).length < min)
                errors.push(new FieldValidationError(`typeMedia[${tm.index}]`, `${tm.label} requires a minimum of ${min} upload${min === 1 ? "" : "s"}.`));
            if (!!max && tm.media.filter((m) => !m.deleted && m.id > 0).length > max)
                errors.push(new FieldValidationError(`typeMedia[${tm.index}]`, `${tm.label} requires a maximum of ${max} upload${max === 1 ? "" : "s"}.`));
            if (!tm.media.filter((m) => !m.deleted && m.id > 0).length)
                errors.push(new FieldValidationError(`typeMedia[${tm.index}]`, `${tm.label} requires an upload.`));
        }
    }

    return errors;
};



// WEBPACK FOOTER //
// ./src/models/abstract/AbstractHelper.ts